// @ts-nocheck: converted from JS

import Button from '@/components/UI/Button';
import MetadataButton from '@/components/UI/MetadataButton';
import Spinner from '@/components/UI/Spinner';
import { Panel, Tab, Tabs } from '@/components/UI/Tabs';
import { successToast } from '@/helpers';
import { _isNil, _isNotEmpty, _notNil } from '@/littledash';
import { Animal } from '@/model/Animal.model.ts';
import InVivoError from '@/model/InVivoError.ts';
import type { Study } from '@/model/Study.model';
import * as Auth from '@/support/auth';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { notAborted, useAbortController } from '@/support/Hooks/fetch/useAbortController';
import useMountedState from '@/support/Hooks/fetch/useMountedState.ts';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import { RouteService } from '@/support/RouteService.ts';
import { isClosed } from '@/support/study';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import React, { Dispatch, ReactNode, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getNextAnimal } from './Show.utils';
import DosingView from './Views/Dosing/DosingView';
import MeasurementView from './Views/MeasurementView';
import ObservationView from './Views/ObservationView';
import SamplesView from './Views/SamplesView';
import type { ShowState, ShowStateAction } from './Workflow.model';
import {
  useChangeAnimalActions,
  useSelectedAnimal,
  useSubjects,
  useUpdateSubjectActions,
  useWorkflowSetup,
  useMeasuredAtInfo,
} from '@/utils/workflow/useWorkflow.tsx';
import { useMeasurementActions } from '@/utils/workflow/useMeasurementActions.tsx';
import { consumptionMetricIds } from '@/components/Workflow/Show/Views/Consumption/Consumption.utils.ts';
import { ClassificationTypes } from '@/model/Treatment.model.ts';

export interface WorkflowStageProps {
  study: Study;
  state: ShowState;
  dispatch: Dispatch<ShowStateAction>;
  openFlash: (res: ReactNode) => void;
  openModal: (modal: string, props: unknown) => void;
  closeModal: () => { type: string };
  focusSearch: () => void;
  openSetupWorkflow: () => void;
  fetchSubjects: () => Promise<void>;
}

const WorkflowStage: React.FC<WorkflowStageProps> = ({
  study,
  state,
  dispatch,
  openFlash,
  openModal,
  closeModal,
  focusSearch,
  openSetupWorkflow,
  fetchSubjects,
}) => {
  const { collections, checklist } = state;
  const { selectedAnimal } = useSelectedAnimal();
  const { changeAnimal, changeCage } = useChangeAnimalActions();
  const {
    workflowSetup: { workflowTabConfig, startTab, foodIntakeEnabled, fluidIntakeEnabled },
  } = useWorkflowSetup();
  const { measured_at, hasMeasuredAtChanged } = useMeasuredAtInfo();
  const { features } = useSelector(({ team: { team, features } }) => ({
    team,
    features,
  }));
  const { updateSingleSubject } = useUpdateSubjectActions();
  const { availableSubjects } = useSubjects();
  const { fetchMeasurements, fetchTodaysMeasurement } = useMeasurementActions();
  const atunesIntegrationEnabled = features.atunes_colony_management_integration === true;

  const hasToggleDosing = useMemo(
    () => workflowTabConfig.find((w) => w.name === 'dosing')?.skipDosing ?? false,
    [workflowTabConfig]
  );
  const getWorkflowIndex = (type) => workflowTabConfig.filter((w) => w.value).findIndex((w) => w.name === type);
  const [subject, setSubject] = useState({});
  const [samples, setSamples] = useState([]);
  const [observations, setObservations] = useState([]);
  const [dosages, setDosages] = useState([]);

  const [loading, setLoading] = useState(true);
  const [unsavedMeasurements, setUnsavedMeasurements] = useState(false);
  const [activeTab, setActiveTab] = useState({ index: 0, clicked: false });
  // Add flag to track deceased marking operation
  const [markingAsDeceased, setMarkingAsDeceased] = useState(false);
  const { newAbortController } = useAbortController();
  const { newAbortController: newSamplesAbortController } = useAbortController();
  const { newAbortController: newObservationsAbortController } = useAbortController();
  const { newAbortController: newDosingAbortController } = useAbortController();
  const isMounted = useMountedState();
  const storeDispatch = useDispatch();
  const { id } = useParams();

  const dataUpdate = {
    samples: (data) => setSamples(data),
    observations: (data) => setObservations(data),
    dosing: (data) => setDosages(data),
  };

  const { response: treatmentsResponse } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/treatments',
    invokeOnInit: true,
    query: { include: ['study_groups', 'metadata'], perPage: -1 },
    path: { studyApiId: study?.api_id },
  });

  const studyTreatments =
    (
      treatmentsResponse?.body as {
        data: Array<Treatment>;
      }
    )?.data ?? [];

  const skipDosingContext = useMemo(() => {
    const dosingWorkflow = workflowTabConfig.find((w) => w.name === 'dosing');
    const dosingTreatments = (dosingWorkflow?.children ?? []).reduce((acc, treatmentApiId) => {
      const treatment = studyTreatments.find((t) => t.api_id === treatmentApiId);
      if (_notNil(treatment)) {
        acc.push(treatment);
      }
      return acc;
    }, []);

    const hasDiseasesOrPreTreatments = dosingTreatments.some(
      (t) =>
        t.treatment_classification === ClassificationTypes.pre_treatment ||
        t.treatment_classification === ClassificationTypes.disease_induction
    );

    const treatmentStudyGroups = dosingTreatments.reduce((acc, t) => {
      t.study_groups?.forEach((g) => acc.add(g.id));
      return acc;
    }, new Set());

    const animalGroupIds = (availableSubjects ?? []).reduce((acc, a) => {
      if (_notNil(a.study_group_id)) {
        acc.add(a.study_group_id);
      }
      return acc;
    }, new Set());

    const hasAnyTreatmentGroupMatch = dosingTreatments.some((t) => {
      const classification = t.treatment_classification ?? ClassificationTypes.treatment;
      return (
        classification === ClassificationTypes.treatment &&
        (t.study_groups?.some((g) => animalGroupIds.has(g.id)) ?? false)
      );
    });

    return { hasDiseasesOrPreTreatments, hasAnyTreatmentGroupMatch, treatmentStudyGroups };
  }, [workflowTabConfig, studyTreatments, availableSubjects]);

  const fetchAnimalDataByType = async (subject: Animal, type: 'samples' | 'observations' | 'dosing') => {
    try {
      switch (type) {
        case 'dosing':
          await Http.get(
            RouteService.api({
              endpoint: 'GET /api/v1/animals/{animalId}/dosages',
              path: { animalId: subject.id },
              query: { sort: 'dosed_at', order: 'desc' },
            }).url.href,
            {
              signal: newDosingAbortController().signal,
            }
          ).then((response) => dataUpdate.dosing(response.data.data));
          break;
        case 'observations':
          await Http.get(
            RouteService.api({
              endpoint: 'GET /api/v1/animals/{animalId}/observations',
              path: { animalId: subject.id },
            }).url.href,
            {
              signal: newObservationsAbortController().signal,
            }
          ).then((response) => dataUpdate.observations(response.data.data));
          break;
        case 'samples':
          await Http.get(
            RouteService.api({
              endpoint: 'GET /api/v1/animals/{animalId}/samples',
              path: { animalId: subject.id },
            }).url.href,
            {
              signal: newSamplesAbortController().signal,
            }
          ).then((response) => dataUpdate.samples(response.data.data));
      }
    } catch (error) {
      if (notAborted(error)) {
        ExceptionHandler.captureException(
          new InVivoError(`Could not load animal data by ${type}`, {
            cause: error,
            slug: `fetch-animal-data-by-${type}`,
          })
        );
      }
    }
  };

  useEffect(() => {
    // Prevent redundant data fetches when marking as deceased
    if (markingAsDeceased) {
      return;
    }

    const selectedSubjectFromMap = availableSubjects.find((s) => Number(s.id) === Number(selectedAnimal));

    // Only update subject if it's actually different (avoid unnecessary remounting)
    if (_notNil(selectedSubjectFromMap) && Number(subject.id) === Number(selectedSubjectFromMap.id)) {
      // Subject ID is the same, check if we need to update other properties
      const hasTerminationChanges = subject.terminated_at !== selectedSubjectFromMap.terminated_at;

      if (!hasTerminationChanges && !hasMeasuredAtChanged) {
        // No significant changes, don't recreate the subject object
        return;
      }
    }

    const subjectCage = state?.collections?.find(
      (cage) =>
        Number(cage.id) ===
        (_notNil(selectedSubjectFromMap?.terminated_at)
          ? Number(selectedSubjectFromMap?.deceasedCageId)
          : Number(selectedSubjectFromMap?.cage_id))
    );
    if (_notNil(subjectCage) && _notNil(selectedSubjectFromMap)) {
      setSubject({ cage: subjectCage, ...selectedSubjectFromMap });
      fetchAllAnimalEntities(selectedAnimal);
    } else {
      setLoading(false);
    }
  }, [
    selectedAnimal,
    workflowTabConfig,
    study,
    availableSubjects,
    markingAsDeceased,
    measured_at,
    hasMeasuredAtChanged,
  ]);

  useEffect(() => {
    setActiveTab({ index: getWorkflowIndex(startTab), clicked: false });
  }, [startTab, workflowTabConfig, selectedAnimal]);

  const updateChecklist = (type, id) => {
    return dispatch({
      type: 'updateChecklist',
      data: { ...checklist, [id]: { [type]: true } },
    });
  };

  const canWrite = !isClosed(study) && Auth.isWriteUserForStudy(study);

  const manageTabWorkflowSwitch = (workflowIndex, clicked) => setActiveTab({ index: workflowIndex, clicked: clicked });

  const onSaveToggles = (workflowTabConfig) =>
    workflowTabConfig.reduce((acc, { name }) => {
      acc[`toggle-${name}`] = () => manageTabWorkflowSwitch(getWorkflowIndex(name), false);
      return acc;
    }, {});

  // Extract data fetching logic for reuse
  const fetchAllAnimalEntities = async (subjectId) => {
    setLoading(true);
    try {
      await Promise.all([
        fetchMeasurements(subjectId),
        _notNil(measured_at) ? fetchTodaysMeasurement(subjectId, measured_at) : Promise.resolve(),
        Http.get(
          RouteService.api({
            endpoint: 'GET /api/v1/animals/{animalId}/samples',
            path: { animalId: subjectId },
          }).url.href,
          {
            signal: newSamplesAbortController().signal,
          }
        ),
        Http.get(
          RouteService.api({
            endpoint: 'GET /api/v1/animals/{animalId}/observations',
            path: { animalId: subjectId },
          }).url.href,
          {
            signal: newObservationsAbortController().signal,
          }
        ),
        Http.get(
          RouteService.api({
            endpoint: 'GET /api/v1/animals/{animalId}/dosages',
            path: { animalId: subjectId },
            query: { sort: 'dosed_at', order: 'desc' },
          }).url.href,
          { signal: newDosingAbortController().signal }
        ),
      ]).then(([, , samplesResponse, observationsResponse, dosagesResponse]) => {
        if (isMounted()) {
          setSamples(samplesResponse?.data?.data ?? []);
          setObservations(observationsResponse?.data?.data ?? []);
          setDosages(dosagesResponse?.data?.data ?? []);
          setLoading(false);
        }
      });
    } catch (error) {
      if (notAborted(error)) {
        setLoading(false);
        ExceptionHandler.captureException(
          new InVivoError('Could not load animal [measurements,samples,observations,dosing]', {
            cause: error,
            slug: 'animal-data-load',
          })
        );
      }
    }
  };

  const onSave = {
    'focus-search': () => focusSearch(),
    'next-subject': () => {
      // If there's only one animal available (due to filtering), refresh current animal data
      if (availableSubjects.length === 1) {
        fetchAllAnimalEntities(selectedAnimal);
        return;
      }

      const nextAnimal = getNextAnimal(selectedAnimal, availableSubjects);
      if (nextAnimal.cage_id !== subject.cage_id && (foodIntakeEnabled || fluidIntakeEnabled)) {
        changeCage({ type: 'SELECT_CAGE', cageId: nextAnimal.cage_id });
      } else {
        changeAnimal({ type: 'SELECT_ANIMAL_CAGE', animalId: nextAnimal.id, cageId: nextAnimal.cage_id });
      }
    },
    ...onSaveToggles(workflowTabConfig),
  };

  const markAsDeceased = async () => {
    closeModal();
    setMarkingAsDeceased(true);
    setLoading(true);
    try {
      const response = await Http.get(apiRoute('studies.show', { id }), {
        signal: newAbortController().signal,
      });
      storeDispatch({
        type: 'SET_CURRENT_STUDY',
        study: response.data.data,
      });
      const {
        data: { data: updatedSubject },
      } = await Http.get(
        apiRoute('studies.animal.show', { studyId: id, id: subject.id }, { include: 'terminated_at_data' })
      );
      updatedSubject['deceasedCageId'] = subject.cage_id;

      // Find cage info and create the final subject object once
      const subjectCage = state?.collections?.find(
        (cage) =>
          Number(cage.id) ===
          (_notNil(updatedSubject?.terminated_at)
            ? Number(updatedSubject?.deceasedCageId)
            : Number(updatedSubject?.cage_id))
      );

      const finalSubject = _notNil(subjectCage) ? { cage: subjectCage, ...updatedSubject } : updatedSubject;

      // Update all states together - only set subject once
      setSubject(finalSubject);
      updateSingleSubject({ ...subject, ...updatedSubject }); // Update context with merged subject data
      setLoading(false);
      setMarkingAsDeceased(false);

      successToast('Successfully marked animal as deceased');
    } catch (error) {
      if (notAborted(error)) {
        setLoading(false);
        setMarkingAsDeceased(false);
        ExceptionHandler.captureException(
          new InVivoError('Could not mark animal as deceased', {
            cause: error,
            slug: 'mark-animal-deceased',
          })
        );
      }
    }
  };

  /**
   *
   * @param view {'measurements', 'samples', 'observations', 'dosages'}
   */
  const handleSave = (view) => {
    switch (view) {
      case 'measurements': {
        onSave?.[workflowTabConfig?.[0]?.onSave]?.();
        break;
      }
      case 'samples': {
        onSave?.[workflowTabConfig?.[1]?.onSave]?.();
        break;
      }
      case 'observations': {
        onSave?.[workflowTabConfig?.[2]?.onSave]?.();
        break;
      }
      case 'dosages': {
        onSave?.[workflowTabConfig?.[3]?.onSave]?.();
        break;
      }
    }
  };

  const renderView: (view: string, clickedInto: boolean) => JSX.Element = (view, clickedInto = false) => {
    switch (view) {
      case 'measurements':
        return (
          <MeasurementView
            setSubject={setSubject}
            subject={subject}
            collections={collections}
            study={study}
            fetchAnimalDataByType={fetchAnimalDataByType}
            openModal={openModal}
            openFlash={openFlash}
            closeModal={closeModal}
            updateChecklist={updateChecklist}
            focusSearch={focusSearch}
            dispatch={dispatch}
            onSave={() => handleSave('measurements')}
            canWrite={canWrite}
            setUnsavedMeasurements={setUnsavedMeasurements}
          />
        );
      case 'samples':
        return (
          <SamplesView
            subject={subject}
            study={study}
            collections={collections}
            samples={samples}
            fetchAnimalDataByType={fetchAnimalDataByType}
            openModal={openModal}
            closeModal={closeModal}
            updateChecklist={updateChecklist}
            measured_at={measured_at}
            focusSearch={focusSearch}
            workflow={workflowTabConfig[1]}
            onSave={() => handleSave('samples')}
            canWrite={canWrite}
          />
        );
      case 'observations':
        return (
          <ObservationView
            subject={subject}
            study={study}
            observations={observations}
            fetchAnimalDataByType={fetchAnimalDataByType}
            openModal={openModal}
            observed_at={measured_at}
            closeModal={closeModal}
            openFlash={openFlash}
            focusSearch={focusSearch}
            workflow={workflowTabConfig[2]}
            onSave={() => handleSave('observations')}
            canWrite={canWrite}
          />
        );
      case 'dosing':
        return (
          <DosingView
            setSubject={setSubject}
            subject={subject}
            study={study}
            dosages={dosages}
            dosed_at={measured_at}
            fetchAnimalDataByType={fetchAnimalDataByType}
            openModal={openModal}
            closeModal={closeModal}
            updateChecklist={updateChecklist}
            workflow={workflowTabConfig[3]}
            dispatch={dispatch}
            onSave={() => handleSave('dosages')}
            canWrite={canWrite}
            openSetupWorkflow={openSetupWorkflow}
            hasToggleDosing={hasToggleDosing}
            isClicked={clickedInto}
            studyTreatments={studyTreatments}
            skipDosingContext={skipDosingContext}
          />
        );
      default:
        return <LoadingAnimal />;
    }
  };

  const consumptionEnabled =
    study?.settings?.calculations?.some((calculation) => consumptionMetricIds.has(calculation.id)) ?? false;

  const MarkAsDeceasedButton = () => (
    <Button
      className="link f6 basier-reg"
      stateless
      disabled={unsavedMeasurements}
      tooltip={unsavedMeasurements ? 'There are unsaved measurements' : ''}
      onClick={() =>
        openModal('EUTHANISE_SUBJECT', {
          subjects: [subject],
          handleCallback: markAsDeceased,
          closeModal,
          consumptionEnabled: consumptionEnabled,
        })
      }
    >
      Mark as deceased
    </Button>
  );

  return (
    <div className="w-60 bg-light-gray flex flex-column" style={{ minHeight: '100%' }}>
      {_isNotEmpty(subject) && _isNotEmpty(workflowTabConfig) && (
        <Tabs outerState={[activeTab.index, (index) => manageTabWorkflowSwitch(index, true)]}>
          <div className="bb b--moon-gray ph3 pt3 bg-white flex">
            <div>
              {workflowTabConfig.reduce((acc, { value, displayName, name }, i) => {
                if (value && name !== 'consumption') {
                  acc.push(
                    <Tab key={i} className="f6" testId={`workflow-${displayName.toLowerCase()}-tab-button`}>
                      {displayName}
                    </Tab>
                  );
                }
                return acc;
              }, [])}
            </div>
            <div className="ml-auto flex">
              {_isNotEmpty(subject.metadata) && (
                <div>
                  <MetadataButton entity={subject} entityName="animalMeta" tooltip="Animal metadata" />
                </div>
              )}
              {_isNil(subject.terminated_at) && (
                <>
                  {!atunesIntegrationEnabled ? <MarkAsDeceasedButton /> : <ExitThroughAtunesButton subject={subject} />}
                </>
              )}
            </div>
          </div>
          {workflowTabConfig.reduce((acc, { value, name }, i) => {
            if (value) {
              acc.push(
                <Panel key={i} className="ui__workflow__expand">
                  {!loading ? renderView(name, activeTab.clicked) : renderView('empty')}
                </Panel>
              );
            }
            return acc;
          }, [])}
        </Tabs>
      )}
    </div>
  );
};
const ExitThroughAtunesButton = ({ subject }) => {
  if (_notNil(subject?.external_termination_url)) {
    const integrationLink = new URL(subject.external_termination_url);

    return (
      <a href={integrationLink} target="_blank" rel="noreferrer">
        tick@lab Exit
      </a>
    );
  }
  return <>tick@lab Exit</>;
};
const LoadingAnimal = () => (
  <div className="flex justify-center items-center h-100">
    <div className="tc ph3 mw6 center">
      <div className="tc">
        <Spinner size="large" color="blue" className="dib" />
        <h3 className="lh-title f4 basier-reg normal pv3">Loading...</h3>
      </div>
    </div>
  </div>
);

export default WorkflowStage;
